import {
  FormControl,
  Select,
  MenuItem,
  Button as MuiButton,
  SelectChangeEvent,
  InputLabel,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useLazySearchLocationsQuery } from "../../../store/services/SearchService";
import { useTypedSelector } from "../../../store/store";
import { createArrayRange } from "../../../utils/helpers/createArrayRange";
import { validateSearch } from "../../../utils/validation";
import LoadingContainer from "../../containers/LoadingContainer";
import {
  AsyncAutocomplete,
  AsyncAutocompleteOption,
} from "../../shared/AsyncAutocomplete";
import CustomTiles from "../../shared/CustomTiles";
import { MonthRangePicker } from "../../shared/MonthRangePicker";

import styles from "./index.module.scss";

interface IInput {
  value: string;
  errorMessage?: string;
}

interface IDateInput {
  value: string[];
  errorMessage?: string;
}

interface IInputs {
  [key: string]: IInput | IDateInput;
  country: IInput;
  date: IDateInput;
  duration: IInput;
}

interface IValidationInputs {
  [key: string]: string;
  country: string;
  date: string;
  duration: string;
}

const DURATION_ITEMS = [
  { value: "1", label: "1" },
  { value: "3-5", label: "3-5" },
  { value: "7", label: "7" },
  { value: "8-10", label: "8-10" },
  { value: "11-14", label: "11-14" },
  { value: "14+", label: "14+" },
];

const parseDurationOptions = (
  value?: string,
): Array<{ value: string; label: string }> => {
  if (!value) {
    return DURATION_ITEMS;
  }

  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => ({ value: v, label: v }));
};

function Search() {
  const [searchLocations] = useLazySearchLocationsQuery();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const {
    start_page_title,
    destination_input_placeholder,
    agent_search_tiles,
    search_bg_image_url,
    search_form_help_text,
    search_duration_options,
  } = useTypedSelector((state) => state.environment);

  const [lastDate, setLastDate] = useState("");

  const [chosenLocations, setChosenLocations] = useState<
    AsyncAutocompleteOption[]
  >([]);

  const [inputs, setInputs] = useState<IInputs>({
    country: { value: "" },
    date: { value: [], errorMessage: "" },
    duration: { value: "" },
  });

  const tiles = useMemo(() => {
    const parsed = JSON.parse(agent_search_tiles);

    return Array.isArray(parsed)
      ? parsed.map((tile: any) => ({ ...tile, target: "_blank" as const }))
      : [];
  }, [agent_search_tiles]);

  const durationItems = useMemo(
    () => parseDurationOptions(search_duration_options),
    [search_duration_options],
  );

  const initLastDate = async () => {
    try {
      const { data } = await searchLocations("");

      if (data?.last_date) {
        setLastDate(data.last_date);
      }
    } catch (error) {
      console.error("initLastDate error: ", error);
    }
  };

  const handleFormError = (errors: IValidationInputs) => {
    const updatedInputs: IInputs = structuredClone(inputs);

    Object.keys(errors).forEach((errorKey) => {
      if (updatedInputs[errorKey]) {
        updatedInputs[errorKey].errorMessage = errors[errorKey];
      }
    });

    setInputs(updatedInputs);
  };

  const fetchDestinations = useCallback(
    async (query: string): Promise<AsyncAutocompleteOption[]> => {
      const q = query.trim();

      if (q.length < 1) {
        return [];
      }

      const { data } = await searchLocations("");

      if (!data?.auto_complete_items) {
        return [];
      }

      return data.auto_complete_items
        .filter((el) => el.name.toLowerCase().includes(q.toLowerCase()))
        .map((item) => ({
          label: item.name,
          value: item.value,
          type: item.type,
          extra_information: item.extra_information,
        }));
    },
    [searchLocations],
  );

  const handleChosenLocationsChange = (items: AsyncAutocompleteOption[]) => {
    setChosenLocations(items);
    setInputs((prev) => ({
      ...prev,
      country: { value: items.map((i) => i.value).join(","), errorMessage: "" },
    }));
  };

  const handleDateChange = (range: [Dayjs | null, Dayjs | null]) => {
    const strDates = [
      range[0]?.format("YYYY-MM") || "",
      range[1]?.format("YYYY-MM") || "",
    ].filter(Boolean);

    setInputs((prev) => ({
      ...prev,
      date: { value: strDates, errorMessage: "" },
    }));
  };

  const handleDurationChange = (e: SelectChangeEvent<unknown>) => {
    setInputs((prev) => ({
      ...prev,
      duration: { value: e.target.value as string, errorMessage: "" },
    }));
  };

  const handleSaveSearch = (payload: {
    duration: string;
    date: string[];
    chosenLocations: any[];
  }) => {
    localStorage.setItem("search", JSON.stringify({ ...payload }));
  };

  const handleSearch = () => {
    const night = (() => {
      switch (true) {
        case inputs.duration.value[0]?.includes("-"): {
          return createArrayRange(inputs.duration.value[0]).join("-");
        }

        case inputs.duration.value[0]?.includes("+"): {
          return inputs.duration.value[0].replace("+", "");
        }

        default: {
          return inputs.duration.value[0];
        }
      }
    })();

    const { country, ship, tag } = (() => {
      const output: Record<string, string[]> = {
        country: [],
        ship: [],
        cruise_tag: [],
      };
      if (chosenLocations.length) {
        chosenLocations.forEach((location) => {
          const type = location.type || "country";

          if (output[type]) {
            output[type].push(location.value);
          }
        });
      }

      return {
        country: output.country.join("-"),
        ship: output.ship.join("-"),
        tag: output.cruise_tag.join("-"),
      };
    })();

    const payload: Record<string, string> = { night, country, ship, tag };

    inputs.date.value.forEach((date, index) => {
      const dateKey = index === 0 ? "start_date" : "end_date";

      payload[dateKey] = date;
    });

    handleSaveSearch({
      duration: inputs.duration.value[0],
      date: inputs.date.value,
      chosenLocations,
    });

    const searchParams = new URLSearchParams();

    for (const key in payload) {
      if (payload[key]) {
        searchParams.append(key, payload[key]);
      }
    }

    navigate(`/search-results?${searchParams.toString()}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    validateSearch({
      data: {
        country: inputs.country.value,
        start_date: inputs.date.value[0],
        end_date: inputs.date.value[1],
        duration: inputs.duration.value,
      },
      onSuccess: handleSearch,
      onError: (errors: IValidationInputs) => {
        const clonedErrors = structuredClone(errors);
        if (clonedErrors.start_date || clonedErrors.end_date) {
          clonedErrors.date = clonedErrors.start_date ?? clonedErrors.end_date;
          delete clonedErrors.start_date;
          delete clonedErrors.end_date;
        }

        handleFormError(clonedErrors);
      },
    });
  };

  useEffect(() => {
    if (!localStorage.getItem("search") || location.search !== "?edit") {
      return;
    }

    const search = JSON.parse(localStorage.getItem("search") ?? "");

    setChosenLocations(search?.chosenLocations ?? []);

    if (search?.duration) {
      setInputs((prev) => ({ ...prev, duration: { value: search.duration } }));
    }

    if (search?.date) {
      setInputs((prev) => ({ ...prev, date: { value: search.date } }));
    }
  }, [location]);

  useEffect(() => {
    (async () => await initLastDate())();
  }, []);

  const dateValue: [Dayjs | null, Dayjs | null] = [
    inputs.date.value[0] ? dayjs(inputs.date.value[0]) : null,
    inputs.date.value[1] ? dayjs(inputs.date.value[1]) : null,
  ];

  const selectStyles = {
    backgroundColor: "var(--input-background-color, #FFFFFF)",
    color: "var(--input-text-color, #000000)",
    borderRadius: 0,
    height: "66px",
    fontFamily: "Montserrat, sans-serif",
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--input-border-color, #cccccc)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--input-focus-border-color, #666666)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--input-focus-border-color, #666666)",
      borderWidth: "1px",
    },
  };

  const buttonStyles = {
    backgroundColor: "var(--search-button-background, #000000)",
    color: "#FFFFFF",
    borderRadius: 0,
    height: "66px",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 600,
    fontSize: "14px",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "var(--search-button-background, #000000)",
      opacity: 0.9,
    },
  };

  return (
    <LoadingContainer isLoading={!lastDate}>
      <div
        className={styles.container}
        style={{ background: `url(${search_bg_image_url}) center/cover` }}>
        <div className={styles.content}>
          <p className={styles.title}>
            {start_page_title || t("start your search")}
          </p>

          <div className={styles.formContainer}>
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.searchInputContainer}>
                <AsyncAutocomplete
                  label={t("Type for ships, countries, companies")}
                  placeholder={
                    destination_input_placeholder ||
                    t("Type for ships, countries, companies")
                  }
                  fetchOptions={fetchDestinations}
                  value={chosenLocations}
                  onChange={handleChosenLocationsChange}
                />
              </div>

              <div className={styles.selectDateContainer}>
                <MonthRangePicker
                  label={t("Select date")}
                  value={dateValue}
                  onChange={handleDateChange}
                  minDate={dayjs().startOf("month")}
                  maxDate={dayjs(dayjs(lastDate).toDate()).startOf("month")}
                  error={!!inputs.date.errorMessage}
                  helperText={inputs.date.errorMessage}
                />
              </div>

              <div className={styles.durationContainer}>
                <FormControl fullWidth error={!!inputs.duration.errorMessage}>
                  <InputLabel
                    id="duration-label"
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      color: "var(--input-placeholder-color, #8C8C8C)",

                      "&.Mui-focused": {
                        color: "var(--input-focus-border-color, #666666)",
                      },
                    }}>
                    {t("Duration")}
                  </InputLabel>

                  <Select
                    labelId="duration-label"
                    label={t("Duration")}
                    value={inputs.duration.value[0] || ""}
                    onChange={handleDurationChange}
                    renderValue={(selected) => selected}
                    sx={selectStyles}
                    MenuProps={{
                      PaperProps: { sx: { borderRadius: 0 } },
                      MenuListProps: { sx: { py: 0 } },
                    }}>
                    {durationItems.map((item) => (
                      <MenuItem
                        key={item.value}
                        value={item.value}
                        sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className={styles.searchButtonContainer}>
                <MuiButton
                  variant="contained"
                  type="submit"
                  fullWidth
                  className={styles.button}
                  sx={buttonStyles}>
                  {t("search")}
                </MuiButton>
              </div>
            </form>

            {search_form_help_text && (
              <p className={styles.formHelpText}>{search_form_help_text}</p>
            )}
          </div>

          <CustomTiles tiles={tiles} />
        </div>
      </div>
    </LoadingContainer>
  );
}

export default Search;
